export default defineAppConfig({
  ui: {
    modal: {
      slots: {
        content: "px-4 py-5 pr-0! pl-0! sm:p-6",
        footer: "flex items-center justify-end gap-1.5 p-4 sm:px-6 pb-0",
        header: "pt-0 min-h-10",
        title: "text-lg font-semibold text-blue-dark",
      },
      variants: {
        fullscreen: {
          false: {
            content:
              "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] rounded-lg shadow-lg ring ring-default min-h-0 overflow-y-auto",
          },
        },
      },
    },
    card: {
      slots: {
        root: "rounded-lg overflow-auto",
      },
    },
    switch: {
      slots: {
        base: "text-sm font-normal text-gray-blue-light! data-[state=unchecked]:bg-gray-dark",
      },
      defaultVariants: {
        size: "xl",
      },
    },
    avatarGroup: {
      slots: {
        base: "ring-0",
      },
    },
    tabs: {
      compoundVariants: [
        {
          orientation: "horizontal",
          variant: "pill",
          class: {
            indicator: "inset-y-0",
            trigger: "min-w-fit, h-8 md:h-11 data-[state=inactive]:text-blue-dark",
            list: "!bg-gray-dark p-0 w-full h-8 md:h-11",
          },
        },
        {
          color: "primary",
          variant: "pill",
          class: {
            indicator: "bg-white",
            trigger: "data-[state=active]:text-primary min-w-fit h-8 md:h-11 data-[state=inactive]:text-blue-dark",
            list: "!bg-gray-dark p-0 w-full h-8 md:h-11",
          },
        },
        {
          color: "primary",
          variant: "link",
          class: {
            list: "justify-center",
          },
        },
      ],
    },
    formField: {
      slots: {
        label: "text-blue-dark text-xs text-gray-blue-light font-bold",
      },
    },
    badge: {
      compoundVariants: [
        {
          color: "neutral",
          variant: "solid",
          class: "text-blue-dark bg-gray-dark",
        },
        {
          color: "success",
          variant: "solid",
          class: "text-green-600 bg-green-200",
        },
        {
          color: "success",
          variant: "soft",
          class: "text-green-600 bg-green-100",
        },
        {
          color: "success",
          variant: "subtle",
          class: "text-green-600 bg-green-100 ring-green-200",
        },
      ],
    },
    input: { slots: { root: "w-full" }, defaultVariants: { size: "lg" } },
    inputMenu: { slots: { root: "w-full" }, defaultVariants: { size: "lg" } },
    textarea: { slots: { root: "w-full" }, defaultVariants: { size: "lg" } },
    select: {
      slots: { base: "w-full", value: "truncate", itemLabel: "text-ellipsis!" },
      defaultVariants: { size: "lg" },
    },
    selectMenu: { slots: { base: "w-full", itemLabel: "text-ellipsis" }, defaultVariants: { size: "lg" } },
    button: {
      compoundVariants: [
        {
          color: "neutral",
          variant: "solid",
          class:
            "active:bg-gray-100 text-gray-700 bg-gray-50 hover:bg-gray-100 disabled:bg-gray-50 aria-disabled:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-0 ring-1 ring-inset ring-gray-300",
        },
        {
          color: "success",
          variant: "solid",
          class: "bg-lime-200 hover:bg-lime-200/80 text-blue-dark",
        },
        {
          color: "success",
          variant: "soft",
          class: "bg-lime-100 hover:bg-lime-100/80 text-blue-dark",
        },
        {
          color: "success",
          variant: "subtle",
          class: "bg-white hover:bg-green-50 text-green-700 ring ring-inset ring-green-300",
        },
        {
          color: "warning",
          variant: "subtle",
          class: "bg-white hover:bg-orange-50 text-orange-700 ring ring-inset ring-orange-300",
        },
      ],
      size: {
        "2xl": "text-lg",
      },
      padding: {
        "2xl": "px-4 py-3",
      },
      square: {
        "2xl": "p-3",
      },
      color: {
        "blue-dark": {
          solid: "bg-blue-dark hover:bg-blue-dark-900",
        },
        lime: {
          solid: "bg-lime",
        },
      },
      variant: {
        "ghost-icon":
          "text-{color} disabled:bg-transparent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-{color}-500",
      },
      defaultVariants: {
        size: "lg",
      },
    },
    dropdownMenu: {
      slots: {
        item: "text-blue-dark",
        itemDescription: "text-blue-dark",
        itemLabel: "text-blue-dark",
        itemLabelExternalIcon: "text-blue-dark",
        itemLeadingIcon: "text-blue-dark",
      },
      variants: {
        active: {
          true: {
            item: "text-blue-dark",
            itemLeadingIcon: "text-blue-dark",
          },
          false: {
            item: "text-blue-dark data-highlighted:text-blue-dark data-[state=open]:text-blue-dark",
            itemLeadingIcon:
              "text-blue-dark group-data-highlighted:text-blue-dark group-data-[state=open]:text-blue-dark",
          },
        },
      },
      compoundVariants: [
        {
          color: "primary",
          active: false,
          class: {
            item: "text-blue-dark data-highlighted:text-blue-dark data-[state=open]:text-blue-dark data-highlighted:before:bg-elevated/50 data-[state=open]:before:bg-elevated/50",
            itemLeadingIcon:
              "text-blue-dark group-data-highlighted:text-blue-dark group-data-[state=open]:text-blue-dark",
          },
        },
        {
          color: "primary",
          active: true,
          class: {
            item: "text-blue-dark before:bg-elevated",
            itemLeadingIcon: "text-blue-dark",
          },
        },
        {
          color: "neutral",
          active: false,
          class: {
            item: "text-blue-dark data-highlighted:text-blue-dark data-[state=open]:text-blue-dark",
            itemLeadingIcon:
              "text-blue-dark group-data-highlighted:text-blue-dark group-data-[state=open]:text-blue-dark",
          },
        },
        {
          color: "neutral",
          active: true,
          class: {
            item: "text-blue-dark",
            itemLeadingIcon: "text-blue-dark",
          },
        },
      ],
      defaultVariants: {
        color: "neutral",
      },
    },
    pagination: {
      slots: {
        root: "gap-1 min-w-[32px] justify-center",
        prev: "text-blue-dark shadow-none bg-transparent ring-0 disabled:invisible hover:bg-transparent",
        next: "text-blue-dark shadow-none bg-transparent ring-0 disabled:invisible hover:bg-transparent",
        first: "text-blue-dark shadow-none bg-transparent ring-0 disabled:invisible hover:bg-transparent",
        last: "text-blue-dark shadow-none bg-transparent ring-0 disabled:invisible hover:bg-transparent",
      },
    },
    colors: {
      primary: "blue-primary",
    },
    progress: {
      slots: {
        indicator:
          "[&::-webkit-progress-bar]:bg-neutral-400/30 [@supports(selector(&::-moz-progress-bar))]:bg-neutral-400/30",
      },
    },
    tooltip: {
      slots: {
        content: "max-w-xs h-auto whitespace-normal",
        text: "whitespace-normal",
        kbds: "inline-flex items-center shrink-0 gap-0.5 not-first-of-type:before:content-[·] not-first-of-type:before:me-0.5",
      },
    },
    accordion: {
      slots: {
        root: "mb-1.5 rounded-lg",
        trigger:
          "text-sm font-normal text-gray-blue-light! bg-primary-50 p-4 rounded-lg data-[state=open]:rounded-b-none",
      },
    },
    checkboxGroup: {
      variants: {
        variant: {
          card: {
            item: "bg-white has-data-[state=checked]:bg-primary/20",
          },
        },
      },
    },
    radioGroup: {
      variants: {
        variant: {
          card: {
            item: "bg-white has-data-[state=checked]:bg-primary/20",
          },
        },
      },
    },
    slideover: {
      slots: {
        body: "scrollbar-thin",
      },
    },
    empty: {
      variants: {
        variant: {
          naked: {
            title: "text-blue-dark",
            description: "text-gray-blue-light",
          },
        },
      },
      defaultVariants: {
        variant: "naked",
      },
    },
    toast: {
      variants: {
        success: {
          root: "outline-primary/25 focus-visible:outline-3 focus-visible:ring-primary",
          icon: "text-primary",
        },
      },
    },
    alert: {
      compoundVariants: [
        {
          color: "warning",
          variant: "soft",
          class: {
            root: "bg-orange-50 text-orange-700",
            icon: "text-orange-500",
          },
        },
        {
          color: "warning",
          variant: "subtle",
          class: {
            root: "bg-orange-50 text-orange-700 ring ring-inset ring-orange-300",
            icon: "text-orange-500",
          },
        },
        {
          color: "success",
          variant: "soft",
          class: {
            root: "bg-green-100 text-green-600",
          },
        },
        {
          color: "success",
          variant: "subtle",
          class: {
            root: "bg-green-100 text-green-600 ring ring-inset ring-green-200",
          },
        },
      ],
    },
  },
});
